/* Altruan Abo-Dashboard v2 — Port des Design-Prototyps (Juli 2026)
 * -----------------------------------------------------------------
 * Live-Version des abgestimmten Prototyps:
 * - Daten & Aktionen über den Backend-Proxy (App Proxy, siehe
 *   backend/server.js). Kein Seal-Token im Browser.
 * - Übersicht bei ≥ 2 Abos, sonst direkt Detail.
 * - Cross-Sell: Shopify Recommendations API (Chips) + Predictive
 *   Search (Suchfeld). Rabatt: "dauerhaft" bekommt denselben echten
 *   Selling-Plan-Rabatt wie das Abo selbst (Backend, priceForMode()),
 *   "einmalig" läuft ohne Rabatt zum vollen Preis.
 * - Segmente: b2b/Firmenkunde kommen aus Liquid (Tags), VIP (≥ 10
 *   Lieferungen) / Stammkunde (≥ 3) werden hier live berechnet.
 *
 * Erwartete Proxy-Antwort: siehe backend/server.js.
 */
(() => {
  "use strict";

  if (customElements.get("altruan-abo-dashboard")) return;

  const esc = (s) =>
    String(s ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );

  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  /* Die beiden Ajax-Endpunkte liefern featured_image UNTERSCHIEDLICH:
     /recommendations/products.json → String ("//cdn.shopify.com/…"),
     /search/suggest.json → Objekt ({ url, alt, width, height }).
     Ohne diese Unterscheidung landete das Objekt per Template-Literal
     als "[object Object]" im src (Suchtreffer ohne Bild). */
  const productImage = (p) => {
    if (!p) return "";
    const fi = p.featured_image;
    return (typeof fi === "string" ? fi : fi?.url) || p.image || "";
  };

  /* Produkte mit mehreren Varianten (z. B. Größen) haben unterschiedliche
     Preise pro Variante, aber weder Suchvorschau noch Empfehlungs-Chips
     bieten eine Größenauswahl — sie zeigen den Produkt-Preis price_min.
     Damit der tatsächlich hinzugefügte Artikel zu diesem Preis passt,
     hier konsequent dieselbe (günstigste verfügbare) Variante wählen,
     nicht einfach die erste im Katalog-Array. */
  const cheapestAvailableVariant = (variants) => {
    const list = (variants || []).filter((v) => v.available !== false);
    if (!list.length) return (variants || [])[0] || null;
    return list.reduce((min, v) => (num(v.price) < num(min.price) ? v : min), list[0]);
  };

  class AltruanAboDashboard extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector("script[data-dash-config]");
      if (!cfgEl) return;
      try {
        this.cfg = JSON.parse(cfgEl.textContent);
      } catch (_) {
        return;
      }

      this.s = this.cfg.strings || {};
      this.fmt = new Intl.NumberFormat(this.cfg.locale || "de", {
        style: "currency",
        currency: this.cfg.currency || "EUR",
      });
      this.dateFmt = new Intl.DateTimeFormat(this.cfg.locale || "de", { dateStyle: "long" });
      this.dateShort = new Intl.DateTimeFormat(this.cfg.locale || "de", { day: "numeric", month: "short" });

      this.subs = [];
      this.openId = null;
      this.recommendations = [];

      this.addEventListener("click", (e) => this.onClick(e));
      this.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.modal) {
          this.closeModal();
          return;
        }
        if (e.key !== "Enter" && e.key !== " ") return;
        const card = e.target.closest("[data-open]");
        if (!card || e.target.closest("[data-rename]")) return;
        e.preventDefault();
        this.openId = card.dataset.open;
        this.renderAll();
      });
      this.addEventListener("input", (e) => {
        if (e.target.matches("[data-add-search]")) this.onSearch(e.target);
      });
      this.addEventListener("submit", (e) => {
        if (e.target.matches("[data-discount-form]")) {
          e.preventDefault();
          this.onDiscount(e.target);
        }
      });

      this.load();
    }

    t(key, vars) {
      let str = this.s[key] || key;
      if (vars)
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp("\\{\\{\\s*" + k + "\\s*\\}\\}", "g"), v);
        }
      return str;
    }

    /* ---------- Daten laden ---------------------------------------- */

    async api(path, options) {
      const url = new URL(this.cfg.endpoint + path, location.origin);
      url.searchParams.set("email", this.cfg.customer.email || "");
      const res = await fetch(
        url,
        Object.assign(
          {
            headers: { "Content-Type": "application/json", Accept: "application/json" },
          },
          options,
        ),
      );
      if (!res.ok) {
        const err = new Error("HTTP " + res.status);
        err.status = res.status;
        try {
          err.body = await res.json();
        } catch (_) {
          err.body = null;
        }
        throw err;
      }
      return res.json();
    }

    /* Eigener Abo-Name: Seal kennt kein Kunden-Namensfeld für Abos
       (das "note"-Feld ist ein Merchant-/Bestell-Notizfeld, kein
       gültiger Update-Action — geprüft), daher rein clientseitig via
       localStorage. Gilt nur in diesem Browser, nicht geräteübergreifend. */
    getAboName(subId) {
      try {
        return localStorage.getItem(`altruan-abo-name-${subId}`) || "";
      } catch (_) {
        return "";
      }
    }
    setAboName(subId, name) {
      try {
        if (name) localStorage.setItem(`altruan-abo-name-${subId}`, name);
        else localStorage.removeItem(`altruan-abo-name-${subId}`);
      } catch (_) {
        /* ignore, z. B. Safari privat */
      }
    }
    startRename(subId) {
      const name = window.prompt(this.t("rename_prompt"), this.getAboName(subId));
      if (name === null) return;
      this.setAboName(subId, name.trim());
      this.renderAll();
    }

    async load() {
      try {
        const data = await this.api("/subscriptions" + this.devQuery());
        this.subs = (data.subscriptions || []).filter((s) => s.status !== "cancelled");
        this.hide("[data-dash-loading]");

        if (!this.subs.length) return this.showEmpty();

        this.openId = this.subs.length === 1 ? this.subs[0].id : null;
        this.loadRecommendations();
        this.resolveItemImages();
        this.renderAll();
      } catch (err) {
        this.hide("[data-dash-loading]");
        const box = this.querySelector("[data-dash-error]");
        box.textContent = this.t("error");
        box.hidden = false;
      }
    }

    /* Shopify Recommendations, geseedet mit dem ersten Abo-Artikel.
       Dedupe gegen alles, was schon in irgendeinem Abo liegt.
       Fallback: leer → Chips-Zone wird ausgeblendet. */
    async loadRecommendations() {
      try {
        const seed = this.subs[0]?.items?.[0]?.product_id;
        if (!seed) return;
        const res = await fetch(
          `${this.cfg.urls.recommendations}?product_id=${encodeURIComponent(seed)}&limit=6&intent=related`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const inSubs = new Set(this.subs.flatMap((s) => (s.items || []).map((i) => String(i.product_id))));
        this.recommendations = (data.products || [])
          .filter((p) => !inSubs.has(String(p.id)) && p.available !== false)
          .slice(0, 3)
          .map((p) => this.normalizeProduct(p));
        this.renderAll();
      } catch (_) {
        /* Chips sind optional */
      }
    }

    /* Recommendations-Ajax-Endpunkt liefert Preise in Cents (kleinste
       Währungseinheit). Predictive Search (/search/suggest.json) liefert
       sie dagegen schon als Dezimalbetrag ("29.99") — kein Sonderfall,
       sondern Shopify-eigene Inkonsistenz zwischen den beiden Endpunkten,
       deshalb der `cents`-Schalter statt einer Heuristik. */
    normalizeProduct(p, { cents = true } = {}) {
      const raw = num(p.price ?? p.price_min);
      const price = cents ? raw / 100 : raw;
      /* price ist price_min (günstigste Variante) — dieselbe Variante
         auch für variantId wählen, sonst weicht der beim Hinzufügen
         tatsächlich verwendete Preis von diesem angezeigten Preis ab. */
      const variantId = cheapestAvailableVariant(p.variants)?.id || p.first_available_variant_id || null;
      /* Verfügbare Varianten (z. B. Größen) mit umgerechnetem Preis —
         damit kann bei Produkten mit mehreren Varianten (Chips) eine
         Auswahl angeboten werden, statt blind die günstigste zu nehmen. */
      const variants = (p.variants || [])
        .filter((v) => v.available !== false)
        .map((v) => ({ id: v.id, title: v.title, price: cents ? num(v.price) / 100 : num(v.price) }));
      return {
        productId: p.id,
        variantId,
        handle: p.handle,
        title: p.title,
        image: productImage(p),
        price,
        variants,
      };
    }

    /* Seal liefert zu Abo-Artikeln WEDER Bild NOCH Handle — die
       Item-Payload kennt nur id/product_id/variant_id/title/
       variant_sku (verifiziert gegen die Merchant-API). Ein
       Produkt-Lookup über die product_id gibt es öffentlich nicht
       (/products/<id>.js → 404), deshalb: über den Titel suchen und
       den Treffer STRIKT über die product_id zuordnen. Kein Treffer
       = kein Bild, aber nie ein falsches. */
    loadItemImages() {
      try {
        return new Map(Object.entries(JSON.parse(localStorage.getItem("altruan-abo-item-images") || "{}")));
      } catch (_) {
        return new Map();
      }
    }

    saveItemImages() {
      try {
        localStorage.setItem("altruan-abo-item-images", JSON.stringify(Object.fromEntries(this.itemImages)));
      } catch (_) {
        /* Bilder sind optional */
      }
    }

    /* Predictive Search bricht bei zu langen Titeln ein (6 Wörter des
       Test-Titels → 0 Treffer, 4 → Treffer), daher gestufte Anfrage
       mit immer kürzerem Präfix. */
    async findProductImage(productId, title) {
      for (const words of [4, 2]) {
        const q = String(title).split(/\s+/).slice(0, words).join(" ");
        if (!q) return "";
        try {
          const res = await fetch(
            `${this.cfg.urls.search}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=10`,
          );
          if (!res.ok) continue;
          const data = await res.json();
          const hit = (data?.resources?.results?.products || []).find((p) => String(p.id) === String(productId));
          const img = productImage(hit);
          if (img) return img;
        } catch (_) {
          /* nächste Stufe versuchen */
        }
      }
      return "";
    }

    async resolveItemImages() {
      this.itemImages = this.itemImages || this.loadItemImages();

      const apply = () => {
        let changed = false;
        for (const sub of this.subs) {
          for (const item of sub.items || []) {
            const cached = this.itemImages.get(String(item.product_id || ""));
            if (cached && !item.image) {
              item.image = cached;
              changed = true;
            }
          }
        }
        return changed;
      };
      apply();

      /* Pro Produkt nur EIN Lookup, auch wenn es in mehreren Abos liegt. */
      const missing = new Map();
      for (const sub of this.subs) {
        for (const item of sub.items || []) {
          const pid = String(item.product_id || "");
          if (!pid || item.image || this.itemImages.has(pid) || !item.title) continue;
          if (!missing.has(pid)) missing.set(pid, item.title);
        }
      }
      if (!missing.size) return;

      await Promise.all(
        [...missing].map(async ([pid, title]) => {
          const img = await this.findProductImage(pid, title);
          if (img) this.itemImages.set(pid, img);
        }),
      );

      this.saveItemImages();
      if (apply()) this.renderAll();
    }

    /* ---------- Aktionen (Proxy) ------------------------------------ */

    /* Optimistisches Einfügen: Zeile erscheint sofort mit _pending:true
       (siehe renderItemRow), noch bevor Seals Antwort da ist. Wird bei
       Erfolg automatisch durch die echten Serverdaten ersetzt (action()
       lädt danach ohnehin frisch), bei Fehlschlag per rollbackOptimisticAdd
       wieder entfernt. sub.items wird direkt mutiert (sub ist dieselbe
       Referenz wie in this.subs). */
    optimisticAdd(sub, { productId, variantId, title, image, shopPrice, finalPrice }) {
      sub.items = sub.items || [];
      sub.items.push({
        id: `_pending_${productId}_${Date.now()}`,
        product_id: productId,
        variant_id: variantId,
        title,
        variant_title: "",
        image,
        quantity: 1,
        one_time: 0,
        delivered: false,
        shop_price: shopPrice,
        abo_price: finalPrice,
        abo_price_now: finalPrice,
        future_price: finalPrice,
        saving_pct: shopPrice > 0 ? Math.round(((shopPrice - finalPrice) / shopPrice) * 100) : 0,
        _pending: true,
      });
    }

    rollbackOptimisticAdd(sub) {
      sub.items = (sub.items || []).filter((i) => !i._pending);
      this.renderAll();
    }

    /* Gemeinsame Hinzufügen-Logik für Empfehlungs-Chips, Suchtreffer und
       die Varianten-Auswahl (siehe renderSearchVariantPicker): einmal
       geschrieben statt dreimal fast identisch. shopPrice ist immer der
       Preis der KONKRET gewählten Variante — nie geraten. */
    async addProductVariant(sub, id, { productId, variantId, handle, title, image, shopPrice }, triggerEl) {
      const finalPrice = Math.round(shopPrice * (1 - this.subscriptionDiscountRate(sub)) * 100) / 100;

      /* Optimistisch sofort anzeigen — Seals Schreib-Aufruf braucht
         erfahrungsgemäß mehrere Sekunden. Das ausgelöste Element
         verschwindet gleichzeitig, sonst wirkt es, als wäre nichts
         passiert. */
      this.optimisticAdd(sub, { productId, variantId, title, image, shopPrice, finalPrice });
      this.recommendations = this.recommendations.filter((p) => String(p.productId) !== String(productId));
      if (triggerEl) triggerEl.remove();
      this.renderAll();

      const ok = await this.action(`/subscription/${id}/items`, {
        variant_id: variantId,
        product_id: productId,
        handle,
        quantity: 1,
        one_time: 0,
        price: finalPrice,
      });
      if (!ok) this.rollbackOptimisticAdd(sub);
      return ok;
    }

    devQuery() {
      try {
        const q = new URLSearchParams(location.search);
        const keep = new URLSearchParams();
        for (const k of ["pay", "status", "days"]) if (q.get(k)) keep.set(k, q.get(k));
        return keep.toString() ? "?" + keep.toString() : "";
      } catch {
        return "";
      }
    }

    async action(path, body, confirmText, doneText) {
      if (confirmText && !window.confirm(confirmText)) return false;
      this.classList.add("is-busy");
      try {
        await this.api(path, { method: "POST", body: JSON.stringify(body || {}) });
        const data = await this.api("/subscriptions" + this.devQuery());
        this.subs = (data.subscriptions || []).filter((s) => s.status !== "cancelled");
        if (this.openId && !this.subs.find((s) => String(s.id) === String(this.openId))) this.openId = null;
        /* Seal liefert nie Bilder mit — jeder Refetch (auch nach
           Pause/Skip/Modus-Wechsel/...) bringt sonst wieder blanke
           Items. resolveItemImages() merged sofort synchron aus dem
           Cache zurück, bevor unten gerendert wird (nur für wirklich
           neue Produkte läuft danach noch ein Nachlade-Request, der
           bei Erfolg selbst neu rendert). */
        this.resolveItemImages();
        if (!this.subs.length) {
          this.showEmpty();
        } else this.renderAll();
        this.toast(doneText || this.t("action_done"));
        return true;
      } catch (err) {
        this.toast(err?.body?.error === "dev-preview" ? err.body.message : this.t("action_failed"), true);
        return false;
      } finally {
        this.classList.remove("is-busy");
      }
    }

    onClick(e) {
      if (this.modalEl && this.modalEl.contains(e.target)) return this.onModalClick(e);
      const rename = e.target.closest("[data-rename]");
      if (rename) {
        this.startRename(rename.dataset.rename);
        return;
      }
      const open = e.target.closest("[data-open]");
      if (open) {
        this.openId = open.dataset.open;
        this.renderAll();
        return;
      }
      if (e.target.closest("[data-back]")) {
        this.openId = null;
        this.renderAll();
        return;
      }
      if (e.target.closest("[data-banner-close]")) {
        const b = e.target.closest(".dash-banner");
        if (b) b.hidden = true;
        return;
      }

      const sub = this.openSub();
      if (!sub) return;
      const id = sub.id;

      if (e.target.closest("[data-charge-now]")) {
        return this.action(`/subscription/${id}/charge-now`, {}, this.t("charge_now_confirm"));
      }
      if (e.target.closest("[data-skip]")) {
        return this.action(`/subscription/${id}/skip-next`, {}, this.t("skip_confirm"));
      }
      /* Pause & Kündigung laufen über das eigene Modal (statt der
         früheren window.confirm-Dialoge): Pause mit Dauer-Auswahl,
         Kündigung als 3-Schritt-Flow Grund → passende Alternative →
         Bestätigung. Fortsetzen bleibt ein Direkt-Klick. */
      const payBtn = e.target.closest("[data-payment-update]");
      if (payBtn) {
        const target = payBtn.dataset.subId ? this.subs.find((s) => String(s.id) === payBtn.dataset.subId) : sub;
        if (!target) return;
        payBtn.disabled = true;
        return this.action(`/subscription/${target.id}/payment-update`, {}, null, this.t("pay_update_sent"));
      }
      if (e.target.closest("[data-pause]")) {
        if (sub.status === "paused") return this.action(`/subscription/${id}/resume`, {});
        this.modalReturnFocus = e.target.closest("[data-pause]");
        return this.openModal({ type: "pause" });
      }
      if (e.target.closest("[data-cancel]")) {
        this.modalReturnFocus = e.target.closest("[data-cancel]");
        return this.openModal({ type: "cancel", step: "reason", reason: "", note: "" });
      }

      /* Neu hinzugefügte Artikel starten als "dauerhaft" (−10 %) — per
         Umschalter (data-setmode) jederzeit auf "einmalig" stellbar.
         Bis zur nächsten Ausführung frei bearbeitbar (siehe
         isLastRecurring()); ob ein Artikel oben in der letzten
         Ausführung erscheint, entscheidet der Server (item.delivered). */
      const add = e.target.closest("[data-add-product]");
      if (add) {
        this.addProductVariant(
          sub,
          id,
          {
            productId: add.dataset.productId,
            variantId: add.dataset.addProduct,
            handle: add.dataset.handle,
            title: add.querySelector(".chip-title")?.textContent || "",
            image: add.querySelector("img.chip-icon")?.getAttribute("src") || null,
            shopPrice: num(add.dataset.price),
          },
          add,
        );
        return;
      }

      /* Chip mit Größen-/Varianten-Auswahl (Produkt hat mehr als eine
         Variante, siehe renderTeaser): Preis kommt aus der GEWÄHLTEN
         Variante, nicht aus dem Produkt-Minimalpreis. */
      const confirmChip = e.target.closest("[data-confirm-chip-variant]");
      if (confirmChip) {
        const picker = confirmChip.closest("[data-chip-picker]");
        const select = picker?.querySelector("[data-variant-select]");
        let variants = [];
        try {
          variants = JSON.parse(picker?.dataset.variants || "[]");
        } catch (_) {
          /* ignore */
        }
        const variant = variants.find((v) => String(v.id) === String(select?.value));
        if (!picker || !variant) return;
        this.addProductVariant(
          sub,
          id,
          {
            productId: picker.dataset.productId,
            variantId: variant.id,
            handle: picker.dataset.handle,
            title: picker.dataset.title,
            image: picker.dataset.image || null,
            shopPrice: variant.price,
          },
          picker,
        );
        return;
      }
      const mode = e.target.closest("[data-setmode]");
      if (mode) {
        const once = mode.dataset.setmode === "once";
        const shopPrice = num(mode.dataset.shopPrice);
        /* "einmalig" läuft ohne Rabatt zum vollen Preis (siehe
           priceForMode() im Backend: gibt für one_time-Artikel den
           unveränderten shopPrice zurück). */
        const price = shopPrice * (once ? 1 : 1 - this.subscriptionDiscountRate(sub));
        return this.action(`/subscription/${id}/items/${mode.dataset.item}`, {
          one_time: once ? 1 : 0,
          price: Math.round(price * 100) / 100,
        });
      }
      const remove = e.target.closest("[data-remove-item]");
      if (remove) {
        return this.action(`/subscription/${id}/items/${remove.dataset.removeItem}/remove`, {});
      }
    }

    async onSearch(input) {
      const box = this.querySelector("[data-add-results]");
      const q = input.value.trim();
      if (!box) return;
      if (q.length < 2) {
        box.innerHTML = "";
        return;
      }
      try {
        const res = await fetch(
          `${this.cfg.urls.search}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4`,
        );
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        const rate = this.subscriptionDiscountRate(this.openSub());
        box.innerHTML = products.length
          ? products
              .map((p) => {
                const n = this.normalizeProduct(p, { cents: false });
                return `
              <button type="button" class="add-result" data-search-add="${esc(n.handle)}" data-price="${n.price}">
                ${n.image ? `<img src="${esc(n.image)}" alt="" loading="lazy">` : ""}
                <span>${esc(n.title)}</span>
                <span class="add-result-price">
                  <s class="side-muted">${this.fmt.format(n.price)}</s>
                  <strong style="color:var(--abo-green-dark)">${this.fmt.format(n.price * (1 - rate))}</strong>
                </span>
              </button>`;
              })
              .join("")
          : `<div class="add-result side-muted">${this.t("teaser_no_results")}</div>`;

        box.querySelectorAll("[data-search-add]").forEach((btn) => {
          btn.addEventListener("click", () => this.onSearchRowClick(btn.dataset.searchAdd, btn), { once: true });
        });
      } catch (_) {
        box.innerHTML = "";
      }
    }

    /* Suchtreffer liefern keine Varianten-Liste → Produkt-JSON
       nachladen. Hat das Produkt nur eine (verfügbare) Variante, direkt
       hinzufügen wie bisher. Bei mehreren (z. B. Größen) erst eine
       Auswahl zeigen, statt blind "irgendeine verfügbare" zu nehmen
       (sonst z. B. Größe S für 3,40 € hinzugefügt, obwohl die Vorschau
       Größe M für 3,21 € versprochen hatte). */
    async onSearchRowClick(handle, rowEl) {
      rowEl.disabled = true;
      rowEl.style.opacity = "0.5";
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        const p = await res.json();
        const available = (p.variants || []).filter((v) => v.available !== false);
        if (!available.length) throw new Error("no variant");
        const image = p.featured_image || p.images?.[0] || null;

        if (available.length === 1) {
          const sub = this.openSub();
          if (!sub) return;
          return this.addProductVariant(
            sub,
            sub.id,
            {
              productId: p.id,
              variantId: available[0].id,
              handle,
              title: p.title,
              image,
              shopPrice: num(available[0].price) / 100,
            },
            rowEl,
          );
        }
        this.renderSearchVariantPicker(rowEl, p, available, image);
      } catch (_) {
        rowEl.disabled = false;
        rowEl.style.opacity = "";
        this.toast(this.t("action_failed"), true);
      }
    }

    /* Ersetzt die Ergebniszeile durch eine Größen-/Varianten-Auswahl +
       Bestätigen-Button. Direkt angehängter Listener (kein data-Attribut-
       Umweg über onClick nötig) — p/available bleiben im Closure. */
    renderSearchVariantPicker(rowEl, p, available, image) {
      const sub = this.openSub();
      const rate = this.subscriptionDiscountRate(sub);
      const sorted = [...available].sort((a, b) => num(a.price) - num(b.price));
      const wrap = document.createElement("div");
      wrap.className = "add-result add-result--picker";
      wrap.innerHTML = `
        <span>${esc(p.title)}</span>
        <select data-variant-select>
          ${sorted.map((v) => `<option value="${esc(v.id)}">${esc(v.title)} — ${this.fmt.format((num(v.price) / 100) * (1 - rate))}</option>`).join("")}
        </select>
        <button type="button" class="picker-confirm" aria-label="${esc(this.t("portal_recos_add"))}">+</button>`;
      rowEl.replaceWith(wrap);
      wrap.querySelector(".picker-confirm").addEventListener(
        "click",
        () => {
          if (!sub) return;
          const select = wrap.querySelector("[data-variant-select]");
          const variant = available.find((v) => String(v.id) === select.value);
          if (!variant) return;
          this.addProductVariant(
            sub,
            sub.id,
            {
              productId: p.id,
              variantId: variant.id,
              handle: p.handle,
              title: p.title,
              image,
              shopPrice: num(variant.price) / 100,
            },
            wrap,
          );
        },
        { once: true },
      );
    }

    onDiscount(form) {
      const sub = this.openSub();
      const input = form.querySelector("input");
      if (!sub || !input.value.trim()) return;
      this.action(`/subscription/${sub.id}/discount-code`, { code: input.value.trim() });
    }

    /* ---------- Rendering -------------------------------------------- */

    openSub() {
      return this.subs.find((s) => String(s.id) === String(this.openId));
    }

    /* Echter, aktuell geltender Abo-Rabatt — abgeleitet aus einem
       bestehenden Artikel dieses Abos, dessen Preis das Backend schon
       anhand der echten Shopify-Selling-Plan-Regeln berechnet hat
       (siehe mapSubscription() im Backend). KEINE feste Prozentzahl:
       je nach Selling Plan des Kunden kann das 10 %, 2 % oder etwas
       ganz anderes sein. Nur eine Schätzung für Cross-Sell-Vorschauen,
       bevor das Backend den echten Preis bestätigt — siehe priceForMode(). */
    subscriptionDiscountRate(sub) {
      const item = (sub?.items || []).find((i) => !i.one_time && num(i.shop_price) > 0);
      if (!item) return 0;
      const rate = (num(item.shop_price) - num(item.abo_price_now ?? item.abo_price)) / num(item.shop_price);
      return Math.max(0, Math.min(1, rate));
    }

    segment() {
      if (this.cfg.segment === "b2b") return "b2b";
      if (this.cfg.segment === "firmenkunde") return "firmenkunde";
      const deliveries = this.subs.reduce((s, x) => s + num(x.completed_orders), 0);
      if (deliveries >= this.cfg.loyalty.vip) return "vip";
      if (deliveries >= this.cfg.loyalty.stammkunde) return "stammkunde";
      return "none";
    }

    renderAll() {
      const root = this.querySelector("[data-dash-root]");
      const sub = this.openSub();
      root.innerHTML =
        this.renderHead() +
        this.renderPaymentBanner() +
        this.renderBanner() +
        this.renderMetrics() +
        (sub ? this.renderDetail(sub) : this.renderOverview());
      root.hidden = false;
    }

    /* ---------- Zahlungsmethode ---------------------------------- */

    paymentBrandLabel(pay) {
      const raw = String(pay.brand || "");
      const map = {
        american_express: "American Express",
        amex: "American Express",
        mastercard: "Mastercard",
        visa: "Visa",
        shoppay: "Shop Pay",
        paypal: "PayPal",
        maestro: "Maestro",
        diners_club: "Diners Club",
        discover: "Discover",
        jcb: "JCB",
      };
      return map[raw.toLowerCase().replace(/[^a-z_]/g, "")] || raw.replace(/_/g, " ");
    }

    paymentExpiryLabel(pay) {
      if (!pay.expiry) return "";
      return String(pay.expiry.month).padStart(2, "0") + "/" + pay.expiry.year;
    }

    /* Warntext für Zustände mit Handlungsbedarf; '' bei ok/unknown. */
    paymentAlert(sub) {
      const pay = sub.payment || {};
      const next = sub.next_billing_date ? this.dateFmt.format(new Date(sub.next_billing_date)) : "";
      switch (pay.state) {
        case "expiring":
          return this.t("pay_alert_expiring", { date: this.paymentExpiryLabel(pay), next });
        case "expired":
          return this.t("pay_alert_expired", { next });
        case "failed":
          return this.t("pay_alert_failed", {
            date: pay.failure?.date ? this.dateFmt.format(new Date(pay.failure.date)) : "",
            reason: this.t("pay_reason_" + (pay.failure?.reason || "other")),
          });
        case "action":
          return this.t("pay_alert_action");
        case "inactive":
          return this.t("pay_alert_inactive", { method: this.paymentBrandLabel(pay) || "PayPal" });
        default:
          return "";
      }
    }

    paymentSeverity(state) {
      return { expiring: "warn", expired: "bad", failed: "bad", action: "bad", inactive: "bad", ok: "ok" }[state] || "";
    }

    renderPaymentCard(sub) {
      const pay = sub.payment || {};
      const sev = this.paymentSeverity(pay.state);
      const alert = this.paymentAlert(sub);
      const brand = this.paymentBrandLabel(pay);
      const line = brand
        ? `${esc(brand)}${pay.last4 ? " ···· " + esc(pay.last4) : ""}${pay.paypal_email ? " · " + esc(pay.paypal_email) : ""}`
        : esc(this.t("pay_on_file"));
      const badge =
        pay.state && pay.state !== "unknown"
          ? `<span class="pay-badge pay-badge--${sev}">${esc(this.t("pay_state_" + pay.state))}</span>`
          : "";
      const expiry = pay.expiry
        ? `<div class="side-muted">${esc(this.t("pay_valid_until", { date: this.paymentExpiryLabel(pay) }))}</div>`
        : "";
      const cta =
        pay.state === "action" && pay.challenge_url
          ? `<a class="btn btn--block pay-cta" href="${esc(pay.challenge_url)}">${esc(this.t("pay_confirm_now"))}</a>`
          : `<button type="button" class="btn ${sev === "ok" || !sev ? "btn--outline " : ""}btn--block pay-cta" data-payment-update>${esc(sev && sev !== "ok" ? this.t("pay_update_now") : this.t("pay_update"))}</button>`;
      return `
        <div class="card${sev && sev !== "ok" ? " card--pay-" + sev : ""}">
          <h2>${esc(this.t("card_payment"))}</h2>
          <div class="pay-row"><span class="side-value">${line}</span>${badge}</div>
          ${expiry}
          ${alert ? `<div class="pay-alert pay-alert--${sev}">${esc(alert)}</div>` : ""}
          ${cta}
          <div class="pay-hint">${esc(this.t("payment_hint"))}</div>
        </div>`;
    }

    /* Abo mit dringendstem Zahlungsproblem (für das Banner oben) */
    paymentIssueSub() {
      const rank = { inactive: 4, failed: 4, action: 4, expired: 3, expiring: 1 };
      return (
        this.subs
          .filter((s) => s.status !== "cancelled" && rank[s.payment?.state])
          .sort((a, b) => rank[b.payment.state] - rank[a.payment.state])[0] || null
      );
    }

    renderPaymentBanner() {
      const sub = this.paymentIssueSub();
      if (!sub) return "";
      const sev = this.paymentSeverity(sub.payment.state);
      const link =
        sub.payment.state === "action" && sub.payment.challenge_url
          ? `<a href="${esc(sub.payment.challenge_url)}">${esc(this.t("pay_confirm_now"))}</a>`
          : `<button type="button" class="dash-banner-link" data-payment-update data-sub-id="${esc(sub.id)}">${esc(this.t("pay_update_now"))}</button>`;
      return `
      <div class="dash-banner dash-banner--pay-${sev}" role="alert">
        <span>${esc(this.paymentAlert(sub))} ${link}</span>
      </div>`;
    }

    renderHead() {
      const seg = this.segment();
      const badges = {
        b2b: { text: this.t("badge_b2b"), cls: "b2b" },
        firmenkunde: { text: this.t("badge_b2b"), cls: "b2b" },
        vip: { text: this.t("badge_vip"), cls: "vip" },
        stammkunde: { text: this.t("badge_stammkunde"), cls: "vip" },
        none: { text: "", cls: "none" },
      }[seg];
      const c = this.cfg.customer;
      return `
      <div class="dash-head">
        <div class="dash-avatar">${esc(c.initials || "·")}</div>
        <div>
          <h1>${esc(this.t("greeting", { name: c.firstName || "" }).trim())}</h1>
          <div class="dash-head-sub">${esc(this.t("customer_since", { year: c.sinceYear }))} · ${c.ordersCount} ${esc(this.t("orders_suffix"))}</div>
        </div>
        <span class="dash-segment dash-segment--${badges.cls}">${esc(badges.text)}</span>
      </div>`;
    }

    renderBanner() {
      const seg = this.segment();
      const saved = this.fmt.format(this.subs.reduce((s, x) => s + num(x.saved_so_far), 0));
      const banners = {
        b2b: {
          cls: "b2b",
          html: `${esc(this.t("banner_b2b_managed"))} <a href="${esc(this.cfg.urls.contact)}">${esc(this.t("banner_b2b_managed_cta"))}</a>`,
        },
        firmenkunde: {
          cls: "b2b",
          html: `${esc(this.t("banner_firmenkunde"))} <a href="${esc(this.cfg.urls.contact)}">${esc(this.t("banner_firmenkunde_cta"))}</a>`,
        },
        vip: { cls: "loyal", html: esc(this.t("banner_vip", { saved })) },
        stammkunde: { cls: "loyal", html: esc(this.t("banner_stammkunde", { saved })) },
        none: {
          cls: "loyal",
          html: `${esc(this.t("banner_default"))} <a href="${esc(this.cfg.urls.home)}">${esc(this.t("banner_default_cta"))}</a>`,
        },
      }[seg];
      return `
      <div class="dash-banner dash-banner--${banners.cls}">
        <span>${banners.html}</span>
        <button type="button" class="dash-banner-close" data-banner-close aria-label="×">✕</button>
      </div>`;
    }

    renderMetrics() {
      const saved = this.subs.reduce((s, x) => s + num(x.saved_so_far), 0);
      const active = this.subs.filter((s) => s.status === "active").length;
      const pausedN = this.subs.filter((s) => s.status === "paused").length;
      /* Nur aktive Abos haben eine nächste Lieferung — Seal entfernt beim
         Pausieren alle geplanten Termine; ein evtl. noch mitgeliefertes
         Datum darf hier nicht als "nächste Lieferung" erscheinen. */
      const dates = this.subs
        .filter((s) => s.status === "active")
        .map((s) => s.next_billing_date)
        .filter(Boolean)
        .sort();
      const next = dates[0]
        ? this.dateShort.format(new Date(dates[0]))
        : pausedN && !active
          ? this.t("status_paused")
          : "—";
      const nextCls = dates[0] ? "" : " dash-metric-value--text";
      return `
      <div class="dash-metrics">
        <div class="dash-metric"><div class="dash-metric-label">${esc(this.t("metric_saved"))}</div>
          <div class="dash-metric-value dash-metric-value--green">${this.fmt.format(saved)}</div></div>
        <div class="dash-metric"><div class="dash-metric-label">${esc(this.t("metric_active"))}</div>
          <div class="dash-metric-value">${active}${pausedN ? ` <span class="dash-metric-sub">${esc(this.t("metric_paused_n", { n: pausedN }))}</span>` : ""}</div></div>
        <div class="dash-metric"><div class="dash-metric-label">${esc(this.t("metric_next"))}</div>
          <div class="dash-metric-value${nextCls}">${esc(next)}</div></div>
      </div>`;
    }

    renderOverview() {
      const MAX_THUMBS = 3;
      const cards = this.subs
        .map((sub) => {
          const items = sub.items || [];
          const shown = items.slice(0, MAX_THUMBS);
          const extra = items.length - shown.length;
          const thumbs =
            shown
              .map((i) => (i.image ? `<img src="${esc(i.image)}" alt="" loading="lazy">` : "<span>·</span>"))
              .join("") + (extra > 0 ? `<span class="overview-thumb-more">+${extra}</span>` : "");
          const pop =
            items.length > 1
              ? `<span class="overview-thumbs-pop">${items
                  .map(
                    (i) => `
              <span class="overview-thumbs-pop-row">
                ${i.image ? `<img src="${esc(i.image)}" alt="" loading="lazy">` : ""}
                <span>${esc(i.title)}</span>
              </span>`,
                  )
                  .join("")}</span>`
              : "";
          const count =
            items.length === 1 ? this.t("overview_items_one") : this.t("overview_items_other", { n: items.length });
          const label = this.getAboName(sub.id) || this.t("abo_default_name");
          const title = [label, count, this.intervalBadge(sub.interval)].filter(Boolean).join(" · ");
          const next =
            sub.status === "paused"
              ? this.t("status_paused")
              : sub.next_billing_date
                ? this.dateFmt.format(new Date(sub.next_billing_date))
                : "—";
          return `
        <div class="overview-card" data-open="${esc(sub.id)}" role="button" tabindex="0">
          <span class="overview-products">
            <span class="overview-thumbs">${thumbs}${pop}</span>
            <span class="overview-info">
              <span class="overview-title-row">
                <span class="overview-title">${esc(title)}</span>
                <button type="button" class="overview-rename" data-rename="${esc(sub.id)}" aria-label="${esc(this.t("rename_abo"))}" title="${esc(this.t("rename_abo"))}">✎</button>
              </span>
              <span class="overview-sub">#${esc(sub.id)}</span>
            </span>
          </span>
          <span class="overview-next">${esc(this.t("overview_next"))}<strong>${esc(next)}</strong></span>
          <span class="overview-price">
            <span class="p">${this.fmt.format(num(sub.totals?.abo))}</span><br>
            <span class="s">${esc(this.t("overview_saves", { amount: this.fmt.format(num(sub.totals?.saving)), pct: sub.totals?.saving_pct || 0 }))}</span>
          </span>
          <span class="overview-side">
            <span class="abo-status abo-status--${esc(sub.status)}">${esc(this.t("status_" + sub.status))}</span>
            <span class="overview-arrow">›</span>
          </span>
        </div>`;
        })
        .join("");

      return `
      <section>
        <div class="overview-list">${cards}</div>
        <div class="overview-new">
          <span>➕</span>
          <span>${esc(this.t("overview_discover"))} <a href="${esc(this.cfg.urls.collection)}">${esc(this.t("overview_discover_cta"))}</a></span>
        </div>
      </section>`;
    }

    renderDetail(sub) {
      const back =
        this.subs.length > 1
          ? `<button type="button" class="detail-back" data-back>← ${esc(this.t("back_to_all"))}</button>`
          : "";
      return `
      <section>
        ${back}
        <div class="dash-grid">
          <div>
            ${this.renderAboCard(sub)}
            ${sub.status === "active" ? this.renderTeaser(sub) : ""}
            ${this.renderLoyalty(sub)}
          </div>
          ${this.renderSidebar(sub)}
        </div>
      </section>`;
    }

    /* Der letzte dauerhafte Artikel bleibt gesperrt (kein Entfernen,
       kein Umstellen auf einmalig) — sonst wäre das Abo nach der
       nächsten Lieferung leer. Dafür gibt es Pause/Kündigung als
       eigene Aktionen. Alle anderen Artikel sind bis zur Ausführung
       frei änderbar; ob ein Artikel schon geliefert wurde (oben
       sichtbar), entscheidet der Server anhand der letzten echten
       Bestellung (item.delivered), nicht mehr der Browser. */
    isLastRecurring(sub, item) {
      if (num(item.one_time) === 1) return false;
      return (sub.items || []).filter((i) => num(i.one_time) !== 1).length <= 1;
    }

    /* Dauerhaft/Einmalig-Umschalter für EINEN Artikel.
       Zeigt den tatsächlich aktuellen Modus als aktiv, nicht immer
       "einmalig". */
    itemControls(item) {
      const once = num(item.one_time) === 1;
      const shopPrice = num(item.shop_price);
      return `
      <span class="mode-switch" role="group">
        <button type="button" class="${!once ? "is-active" : ""}" data-setmode="always" data-item="${esc(item.id)}" data-shop-price="${shopPrice}">${esc(this.t("mode_always"))}</button>
        <button type="button" class="${once ? "is-active" : ""}" data-setmode="once" data-item="${esc(item.id)}" data-shop-price="${shopPrice}">${esc(this.t("mode_once"))}</button>
      </span>`;
    }

    renderItemRow(item, opts) {
      const o = opts || {};
      const hasSaving = num(item.shop_price) > num(item.abo_price);
      const price = o.futurePrice != null ? o.futurePrice : num(item.abo_price);
      return `
      <div class="abo-item ${o.added ? "abo-item--added" : ""} ${item._pending ? "abo-item--pending" : ""}">
        ${item.image ? `<img class="abo-item-img" src="${esc(item.image)}" alt="" loading="lazy">` : '<div class="abo-item-img">·</div>'}
        <div class="abo-item-info">
          <div class="abo-item-title">${esc(item.title)}${item._pending ? `<span class="abo-item-pending-badge">${esc(this.t("item_pending"))}</span>` : ""}</div>
          <div class="abo-item-qty">${esc(item.variant_title || "")}${item.quantity > 1 ? " · " + item.quantity + " ×" : ""}</div>
          ${o.lockedHint ? `<div class="abo-item-qty">${esc(this.t("last_item_locked"))}</div>` : ""}
        </div>
        ${o.controls || ""}
        <div class="abo-item-prices">
          ${hasSaving ? `<span class="abo-price-compare">${this.fmt.format(num(item.shop_price))}</span> ` : ""}
          <span class="abo-price ${hasSaving ? "" : "abo-price--plain"}">${this.fmt.format(price)}</span>${
            hasSaving && item.saving_pct ? `<span class="abo-save-badge">−${item.saving_pct}%</span>` : ""
          }
        </div>
        ${o.removable ? `<button type="button" class="item-remove" data-remove-item="${esc(item.id)}" aria-label="${esc(this.t("remove_item"))}">✕</button>` : ""}
      </div>`;
    }

    renderAboCard(sub) {
      /* Oben = die letzte tatsächliche Ausführung: nur Artikel, die in
         der letzten abgerechneten Bestellung waren (item.delivered, vom
         Server aus der echten Shopify-Bestellung ermittelt). Bewusst
         schreibgeschützt — Ändern/Entfernen geht unten in "Deine
         nächste Lieferung". Summen nur aus diesen Artikeln, nicht aus
         sub.totals (die zählen auch neu hinzugefügte mit). */
      const items = (sub.items || []).filter((i) => i.delivered);
      const shopTotal = items.reduce((s, i) => s + num(i.shop_price) * (i.quantity || 1), 0);
      const aboTotal = items.reduce((s, i) => s + num(i.abo_price) * (i.quantity || 1), 0);
      const saving = Math.max(0, shopTotal - aboTotal);
      const savingPct = shopTotal > 0 ? Math.round((saving / shopTotal) * 100) : 0;
      const rows = items.map((i) => this.renderItemRow(i)).join("");
      const paused = sub.status === "paused";
      return `
      <article class="card">
        <div class="abo-card-head">
          <span class="overview-title-row">
            <strong class="abo-card-name">${esc(this.getAboName(sub.id) || this.t("abo_default_name"))}</strong>
            <button type="button" class="overview-rename" data-rename="${esc(sub.id)}" aria-label="${esc(this.t("rename_abo"))}" title="${esc(this.t("rename_abo"))}">✎</button>
          </span>
          <span class="abo-card-interval">${esc(this.intervalLabel(sub.interval))}</span>
          <span class="abo-card-id">#${esc(sub.id)}${sub.created_at ? " · " + esc(this.t("since")) + " " + this.dateFmt.format(new Date(sub.created_at)) : ""}${
            sub.last_delivery?.date
              ? " · " + esc(this.t("last_delivery")) + " " + this.dateFmt.format(new Date(sub.last_delivery.date))
              : ""
          }</span>
          <span class="abo-status abo-status--${esc(sub.status)}">${esc(this.t("status_" + sub.status))}</span>
        </div>
        ${paused ? this.renderPausedNote(sub, saving) : ""}
        ${rows}
        <div class="abo-summary">
          ${saving > 0 ? `<div class="abo-summary-row"><span>${esc(this.t("shop_price"))}</span><s>${this.fmt.format(shopTotal)}</s></div>` : ""}
          <div class="abo-summary-row"><span>${esc(this.t("abo_price"))}</span><strong>${this.fmt.format(aboTotal)}</strong></div>
          ${
            saving > 0
              ? `
          <div class="abo-summary-row abo-summary-row--save"><span>${esc(this.t("you_save"))}</span>
            <strong>${this.fmt.format(saving)} ${esc(this.t("per_delivery"))} <span class="abo-save-badge">−${savingPct}%</span></strong>
          </div>`
              : ""
          }
          <div class="abo-summary-row"><span>${esc(this.t("delivery_cost"))}</span><strong>${this.fmt.format(num(sub.delivery_price))}</strong></div>
          <div class="abo-summary-row abo-summary-row--total"><span>${esc(this.t("total_per_delivery"))}</span>
            <strong>${this.fmt.format(aboTotal + num(sub.delivery_price))}</strong></div>
        </div>
        ${
          sub.pending_discount
            ? `<div class="abo-note">✓ ${esc(
                this.t("pending_discount", {
                  cycle: sub.pending_discount.from_cycle,
                  percent: sub.pending_discount.pct,
                }),
              )}</div>`
            : ""
        }
        <div class="abo-foot">
          ${sub.next_billing_date && !paused ? `<span class="abo-next">${esc(this.t("next_billing"))}: <strong>${this.dateFmt.format(new Date(sub.next_billing_date))}</strong></span>` : ""}
          ${!paused ? `<button type="button" class="abo-skip" data-skip>${esc(this.t("skip_next"))}</button>` : ""}
        </div>
      </article>`;
    }

    /* Wie viele reguläre Liefertermine seit der Pause ausgefallen sind.
       Seal holt ausgesetzte Lieferungen beim Fortsetzen nicht nach
       (verifiziert an Abo-Historien, 17.09.2026), daher ist die Ersparnis
       dieser Termine tatsächlich entgangen. Näherung über die
       Intervall-Länge ab paused_on. */
    pauseMissed(sub) {
      if (sub.status !== "paused" || !sub.paused_on) return 0;
      const days = { day: 1, week: 7, month: 30.4375, year: 365.25 }[sub.interval?.unit] || 7;
      const len = Math.max(1, num(sub.interval?.every) || 1) * days;
      const elapsed = (Date.now() - new Date(sub.paused_on).getTime()) / 86400000;
      return Number.isFinite(elapsed) ? Math.max(0, Math.floor(elapsed / len)) : 0;
    }

    /* Hinweisblock im pausierten Zustand: seit wann, was entgeht,
       was beim Fortsetzen passiert, Fortsetzen-Button. */
    renderPausedNote(sub, saving) {
      const missed = this.pauseMissed(sub);
      const since = sub.paused_on ? this.dateFmt.format(new Date(sub.paused_on)) : null;
      const b = (v) => `<strong>${esc(this.fmt.format(v))}</strong>`;
      let save = "";
      if (saving > 0) {
        save =
          missed >= 1
            ? this.t(missed === 1 ? "paused_missed_one" : "paused_missed", { n: missed, sum: b(saving * missed) })
            : this.t("paused_not_saving", { amount: b(saving) });
      }
      return `
        <div class="abo-paused">
          <p class="abo-paused__title">${esc(since ? this.t("paused_since", { date: since }) : this.t("paused_title"))}</p>
          ${save ? `<p class="abo-paused__save">${save}</p>` : ""}
          <p class="abo-paused__info">${esc(this.t("paused_resume_info"))}</p>
          <button type="button" class="btn btn--amber" data-pause>${esc(this.t("paused_resume_now"))}</button>
        </div>`;
    }

    renderTeaser(sub) {
      const nextDate = sub.next_billing_date ? this.dateFmt.format(new Date(sub.next_billing_date)) : "";
      /* Nächste Lieferung = ALLE Artikel, bis zur Ausführung frei
         änderbar (Entfernen + Dauerhaft/Einmalig). Einzige Ausnahme:
         der letzte dauerhafte Artikel (siehe isLastRecurring). Noch
         nicht gelieferte Artikel (item.delivered === false) werden
         hervorgehoben; Zeilen, die gerade erst an Seal gehen
         (_pending), bekommen noch keine Kontrollen. */
      const rows = (sub.items || [])
        .map((i) => {
          const locked = this.isLastRecurring(sub, i);
          const editable = !locked && !i._pending;
          return this.renderItemRow(i, {
            added: !i.delivered,
            removable: editable,
            controls: editable ? this.itemControls(i) : "",
            lockedHint: locked && (sub.items || []).length > 1,
            futurePrice: !i.one_time && i.future_price != null ? num(i.future_price) : undefined,
          });
        })
        .join("");

      const discountRate = this.subscriptionDiscountRate(sub);
      /* Produkte mit mehreren Varianten (z. B. Größen) bekommen eine
         Auswahl statt eines Sofort-Hinzufügen-Buttons — sonst landet
         eine zufällige Variante im Abo, deren Preis nicht zum
         angezeigten passt (siehe cheapestAvailableVariant-Kommentar). */
      const chips = this.recommendations
        .map((p) => {
          if ((p.variants || []).length > 1) {
            const sorted = [...p.variants].sort((a, b) => a.price - b.price);
            const options = sorted
              .map(
                (v) =>
                  `<option value="${esc(v.id)}">${esc(v.title)} — ${this.fmt.format(v.price * (1 - discountRate))}</option>`,
              )
              .join("");
            return `
          <div class="add-chip add-chip--picker" data-chip-picker
               data-product-id="${esc(p.productId)}" data-handle="${esc(p.handle || "")}"
               data-title="${esc(p.title)}" data-image="${esc(p.image || "")}"
               data-variants="${esc(JSON.stringify(p.variants))}">
            ${p.image ? `<img class="chip-icon" src="${esc(p.image)}" alt="" loading="lazy">` : '<span class="chip-icon">·</span>'}
            <span class="chip-title">${esc(p.title)}</span>
            <select data-variant-select>${options}</select>
            <button type="button" class="chip-plus" data-confirm-chip-variant aria-label="${esc(this.t("portal_recos_add"))}">+</button>
          </div>`;
          }
          return `
        <button type="button" class="add-chip" data-add-product="${esc(p.variantId || "")}" data-product-id="${esc(p.productId)}" data-handle="${esc(p.handle || "")}" data-price="${p.price}" ${p.variantId ? "" : "disabled"}>
          <span class="chip-plus">+</span>
          ${p.image ? `<img class="chip-icon" src="${esc(p.image)}" alt="" loading="lazy">` : '<span class="chip-icon">·</span>'}
          <span class="chip-title">${esc(p.title)}</span>
          <span class="chip-prices">
            <span class="chip-compare">${this.fmt.format(p.price)}</span>
            <span class="chip-price">${this.fmt.format(p.price * (1 - discountRate))}</span>
            <span class="abo-save-badge">−${Math.round(discountRate * 100)}%</span>
          </span>
        </button>`;
        })
        .join("");

      /* Vollständige Summen-Aufschlüsselung für die nächste Lieferung —
         genau wie oben in renderAboCard, nur eben inkl. aller neu
         hinzugefügten Artikel. Shop-Preis kommt live vom Server. */
      const nextItems = (sub.items || []).map((i) => {
        const nextAboPrice = i.one_time ? num(i.abo_price) : num(i.future_price != null ? i.future_price : i.abo_price);
        return { shopPrice: num(i.shop_price), nextAboPrice, quantity: i.quantity || 1 };
      });
      const shopTotal = nextItems.reduce((s, i) => s + i.shopPrice * i.quantity, 0);
      const aboTotal = nextItems.reduce((s, i) => s + i.nextAboPrice * i.quantity, 0);
      const saving = Math.max(0, shopTotal - aboTotal);
      const savingPct = shopTotal > 0 ? Math.round((saving / shopTotal) * 100) : 0;
      const total = aboTotal + num(sub.delivery_price);

      return `
      <article class="abo-teaser">
        <div class="abo-teaser-head">
          <span class="abo-teaser-title">${esc(this.t("teaser_title"))}${nextDate ? " · " + esc(nextDate) : ""}</span>
          ${sub.pending_discount ? `<span class="abo-teaser-tag">${esc(this.t("teaser_tag_then", { pct: sub.pending_discount.pct }))}</span>` : ""}
        </div>
        ${rows}
        <div class="teaser-add">
          <div class="teaser-add-label">🛒 ${esc(this.t("teaser_add_title"))}
            <span class="side-muted">${esc(this.t("teaser_add_sub"))}</span>
          </div>
          <input type="text" data-add-search placeholder="${esc(this.t("teaser_search_placeholder"))}" autocomplete="off" data-lpignore="true" data-1p-ignore data-bwignore="true" data-form-type="other">
          <div class="teaser-add-results" data-add-results></div>
          ${chips ? `<div class="teaser-add-chips">${chips}</div>` : ""}
        </div>
        <div class="abo-summary">
          ${saving > 0 ? `<div class="abo-summary-row"><span>${esc(this.t("shop_price"))}</span><s>${this.fmt.format(shopTotal)}</s></div>` : ""}
          <div class="abo-summary-row"><span>${esc(this.t("abo_price"))}</span><strong>${this.fmt.format(aboTotal)}</strong></div>
          ${
            saving > 0
              ? `
          <div class="abo-summary-row abo-summary-row--save"><span>${esc(this.t("you_save"))}</span>
            <strong>${this.fmt.format(saving)} ${esc(this.t("per_delivery"))} <span class="abo-save-badge">−${savingPct}%</span></strong>
          </div>`
              : ""
          }
          <div class="abo-summary-row"><span>${esc(this.t("delivery_cost"))}</span><strong>${this.fmt.format(num(sub.delivery_price))}</strong></div>
          <div class="abo-summary-row abo-summary-row--total"><span>${esc(this.t("teaser_total"))}</span>
            <strong>${this.fmt.format(total)}</strong></div>
        </div>
        <div class="abo-teaser-foot">
          <span class="abo-teaser-hint">${esc(this.t("teaser_hint"))}</span>
          <button type="button" class="abo-teaser-cta" data-charge-now>${esc(this.t("charge_now"))}</button>
        </div>
      </article>`;
    }

    renderLoyalty(sub) {
      const pd = sub.pending_discount;
      if (!pd || !pd.pct) return "";
      const done = num(sub.completed_orders);
      const target = pd.from_cycle;
      if (done >= target) return "";
      const pctBar = Math.min(100, Math.round((done / target) * 100));
      return `
      <div class="dash-loyalty">
        <span>★</span>
        <span style="flex:1">${esc(this.t("loyalty_preview", { cycle: target, pct: pd.pct, left: target - done }))}</span>
        <div class="dash-loyalty-bar"><div style="width:${pctBar}%"></div></div>
      </div>`;
    }

    renderSidebar(sub) {
      const c = sub.customer || {};
      const ship = sub.shipping || {};
      const paused = sub.status === "paused";
      const manage = sub.manage_url
        ? `<a class="card-edit" href="${esc(sub.manage_url)}">${esc(this.t("edit"))}</a>`
        : "";
      return `
      <aside>
        <div class="card">
          ${manage}
          <h2>${esc(this.t("card_customer"))}</h2>
          <div class="side-value">${esc(c.name || this.cfg.customer.firstName || "")}</div>
          <div class="side-muted">${esc(c.email || this.cfg.customer.email)}</div>
        </div>
        <div class="card">
          ${manage}
          <h2>${esc(this.t("card_shipping"))}</h2>
          ${ship.name ? `<div class="side-value">${esc(ship.name)}</div>` : ""}
          ${ship.address1 ? `<div class="side-value">${esc(ship.address1)}</div>` : ""}
          ${ship.zip || ship.city ? `<div class="side-value">${esc((ship.zip || "") + " " + (ship.city || ""))}${ship.country ? ", " + esc(ship.country) : ""}</div>` : ""}
        </div>
        <div class="card">
          <h2>${esc(this.t("card_discount"))}</h2>
          <form data-discount-form class="discount-row">
            <input type="text" placeholder="${esc(this.t("discount_placeholder"))}" aria-label="${esc(this.t("card_discount"))}">
            <button type="submit" class="btn">${esc(this.t("discount_apply"))}</button>
          </form>
          ${sub.discount_code ? `<div class="side-muted" style="margin-top:0.8rem">✓ ${esc(this.t("discount_applied"))}: <strong>${esc(sub.discount_code)}</strong></div>` : ""}
        </div>
        ${this.renderPaymentCard(sub)}
        <div class="card">
          <h2>${esc(this.t("card_actions"))}</h2>
          <button type="button" class="action-pause" data-pause>${esc(paused ? this.t("resume") : this.t("pause"))}</button>
          <button type="button" class="action-cancel" data-cancel>${esc(this.t("cancel"))}</button>
        </div>
      </aside>`;
    }

    /* ---------- Modal: Pause & Kündigung ------------------------------
       Ein Overlay im Dashboard-Look statt window.confirm. Zustand liegt
       in this.modal ({type:'pause'|'cancel', step, reason, note, choice}).
       Schrittwechsel rendern den Box-Inhalt neu; Backdrop und Box selbst
       bleiben stehen (sonst liefe die Einblend-Animation bei jedem Klick
       erneut). Die Auswahl einer Option (Radio) wird in place aktualisiert
       (onModalChange), damit Fokus und Scrollposition erhalten bleiben.
       Der Kündigungs-Flow bleibt trotz Rückhalte-Angeboten kurz: ab
       Schritt 2 ist "Trotzdem kündigen" sichtbar, Schritt 3 ist die
       Bestätigung — Kündigen darf nicht schwerer sein als Abschließen. */
    openModal(state) {
      this.modal = state;
      this.renderModal(true);
    }

    closeModal() {
      if (this.modalEl) {
        this.modalEl.remove();
        this.modalEl = null;
      }
      this.modal = null;
      document.body.classList.remove("dash-modal-open");
      const back = this.modalReturnFocus;
      this.modalReturnFocus = null;
      if (back && back.isConnected) {
        try {
          back.focus();
        } catch (_) {
          /* egal */
        }
      }
    }

    renderModal(focusInitial) {
      if (!this.modal) return;
      const sub = this.openSub();
      if (!sub) return this.closeModal();
      if (!this.modalEl) {
        this.modalEl = document.createElement("div");
        this.modalEl.className = "dash-modal";
        this.modalEl.addEventListener("change", (e) => this.onModalChange(e));
        this.modalEl.addEventListener("input", (e) => {
          if (e.target.matches("[data-modal-msg]")) {
            this.modal.msg = e.target.value;
            const send = this.modalEl.querySelector('[data-modal-offer="message-send"]');
            if (send) send.disabled = String(this.modal.msg || "").trim().length < 5;
            return;
          }
          if (!e.target.matches("[data-modal-note]")) return;
          this.modal.note = e.target.value;
          const next = this.modalEl.querySelector('[data-modal-step="offer"]');
          if (next) next.disabled = !this.canContinueCancel();
        });
        this.appendChild(this.modalEl);
        document.body.classList.add("dash-modal-open");
      }
      const body = this.modal.type === "pause" ? this.renderPauseModal(sub) : this.renderCancelModal(sub);
      let box = this.modalEl.querySelector(".dash-modal__box");
      if (!box) {
        this.modalEl.innerHTML = `
          <div class="dash-modal__backdrop" data-modal-close></div>
          <div class="dash-modal__box" role="dialog" aria-modal="true" aria-labelledby="dash-modal-title" tabindex="-1"></div>`;
        box = this.modalEl.querySelector(".dash-modal__box");
      }
      box.innerHTML = `
          <button type="button" class="dash-modal__x" data-modal-close aria-label="${esc(this.t("modal_close"))}">×</button>
          ${body}`;
      box.scrollTop = 0;
      if (focusInitial) {
        const target =
          this.modalEl.querySelector("[data-modal-focus]") || this.modalEl.querySelector(".dash-modal__box");
        if (target) target.focus();
      }
    }

    onModalClick(e) {
      if (e.target.closest("[data-modal-close]")) return this.closeModal();
      const sub = this.openSub();
      if (!sub) return this.closeModal();
      const step = e.target.closest("[data-modal-step]");
      if (step) {
        this.modal.step = step.dataset.modalStep;
        return this.renderModal(true);
      }
      const offer = e.target.closest("[data-modal-offer]");
      if (offer) return this.applyOffer(sub, offer.dataset.modalOffer);
      if (e.target.closest("[data-payment-update]")) {
        this.closeModal();
        return this.action(`/subscription/${sub.id}/payment-update`, {}, null, this.t("pay_update_sent"));
      }
      if (e.target.closest("[data-modal-pause-submit]")) return this.submitPause(sub);
      if (e.target.closest("[data-modal-cancel-submit]")) return this.submitCancel(sub);
    }

    /* Optionswahl ohne Neu-Rendern: Markierung umschalten, Zusatzfeld
       (Freitext bei "Anderer Grund", Datum bei "Pausieren bis") nur an
       der gewählten Option ein-/ausblenden, Weiter-Button aktualisieren.
       Fokus bleibt auf dem Radio (Pfeiltasten funktionieren weiter) bzw.
       springt ins Zusatzfeld. */
    onModalChange(e) {
      if (!this.modal) return;
      const name = e.target.name;
      if (name === "pause-until") {
        this.modal.until = e.target.value;
        this.modal.untilError = false;
        e.target.classList.remove("is-invalid");
        this.modalEl.querySelector(".dash-opt__error")?.remove();
        return;
      }
      if (name !== "pause-choice" && name !== "cancel-reason") return;
      const value = e.target.value;
      if (name === "pause-choice") this.modal.choice = value;
      else this.modal.reason = value;
      this.modalEl.querySelectorAll(`input[name="${name}"]`).forEach((inp) => {
        inp.closest(".dash-opt")?.classList.toggle("is-checked", inp.checked);
      });
      const extraKey = name === "cancel-reason" ? "other" : "until";
      const extraSel = name === "cancel-reason" ? "[data-modal-note]" : "[data-modal-until]";
      let extra = this.modalEl.querySelector(extraSel);
      if (value !== extraKey) {
        extra?.closest(".dash-opt__extra")?.remove();
      } else if (!extra) {
        const body = e.target.closest(".dash-opt")?.querySelector(".dash-opt__body");
        const sub = this.openSub();
        if (body && sub) {
          body.insertAdjacentHTML(
            "beforeend",
            `<span class="dash-opt__extra">${name === "cancel-reason" ? this.cancelNoteField() : this.pauseDateField(sub)}</span>`,
          );
          extra = this.modalEl.querySelector(extraSel);
        }
      }
      const next = this.modalEl.querySelector('[data-modal-step="offer"]');
      if (next) next.disabled = !this.canContinueCancel();
      if (value === extraKey && extra) extra.focus();
    }

    /* Freitextfeld für "Anderer Grund" (Pflicht, mind. 5 Zeichen) */
    cancelNoteField() {
      const m = this.modal || {};
      return `<textarea class="dash-opt__note" data-modal-note rows="2" maxlength="300" required placeholder="${esc(this.t("reason_note_placeholder"))}">${esc(m.note || "")}</textarea>
          <span class="dash-opt__req">${esc(this.t("reason_note_required"))}</span>`;
    }

    /* Datumsfeld für "Pausieren bis" */
    pauseDateField(sub) {
      const m = this.modal || {};
      const range = this.pauseUntilRange(sub);
      return `<input type="date" class="dash-opt__date${m.untilError ? " is-invalid" : ""}" name="pause-until" data-modal-until
          min="${range.min}" max="${range.max}" value="${esc(m.until || "")}" aria-label="${esc(this.t("pause_until"))}">
        ${m.untilError ? `<span class="dash-opt__error">${esc(this.t("pause_until_invalid"))}</span>` : ""}`;
    }

    renderOpt(name, o, checked, extra) {
      return `<label class="dash-opt${checked ? " is-checked" : ""}">
        <input type="radio" name="${name}" value="${esc(o.key)}" ${checked ? "checked" : ""}>
        <span class="dash-opt__body">
          <span class="dash-opt__label">${esc(o.label)}</span>
          ${o.hint ? `<span class="dash-opt__hint">${esc(o.hint)}</span>` : ""}
          ${extra ? `<span class="dash-opt__extra">${extra}</span>` : ""}
        </span>
      </label>`;
    }

    /* Spar-Hinweis ("Du hast bereits X gespart …"): pro-Lieferung-Vorteil
       und bisherige Ersparnis, fett hervorgehoben. Ohne Vorteil (0 €)
       entfällt der Hinweis — dann gäbe es nichts zu verlieren. */
    savingsCallout(sub, keySaved, keyNew) {
      const perDelivery = num(sub.totals?.saving);
      const savedSoFar = num(sub.saved_so_far);
      if (perDelivery <= 0 && savedSoFar <= 0) return "";
      const b = (v) => `<strong>${esc(this.fmt.format(v))}</strong>`;
      const text =
        savedSoFar > 0
          ? this.t(keySaved, { saved: b(savedSoFar), amount: b(perDelivery) })
          : this.t(keyNew, { amount: b(perDelivery) });
      return `<p class="dash-saved">${text}</p>`;
    }

    /* Frühestes Datum für "Pausieren bis": Tag nach dem nächsten Termin
       (früher wäre keine Pause, sondern ein Vorziehen). Spätestens 12
       Monate voraus. Format YYYY-MM-DD in lokaler Zeit. */
    pauseUntilRange(sub) {
      const iso = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const next = sub.upcoming_dates?.[0] ? new Date(sub.upcoming_dates[0]) : new Date();
      const min = new Date(next);
      min.setDate(min.getDate() + 1);
      const max = new Date();
      max.setMonth(max.getMonth() + 12);
      return { min: iso(min), max: iso(max) };
    }

    /* Pause: 1–3 Lieferungen aussetzen (technisch: Skip der nächsten
       Termine — Abo läuft danach von selbst weiter, niemand muss ans
       Fortsetzen denken), bis zu einem Wunschdatum (technisch: nächsten
       Termin verschieben, Folgetermine rücken mit) oder unbegrenzt
       pausieren (Seal-Pause). */
    renderPauseModal(sub) {
      const m = this.modal;
      const dates = sub.upcoming_dates || [];
      const opts = [1, 2, 3]
        .filter((n) => dates[n])
        .map((n) => ({
          key: "skip" + n,
          label: n === 1 ? this.t("pause_skip_one") : this.t("pause_skip_n", { n }),
          hint: this.t("pause_skip_hint", { date: this.dateFmt.format(new Date(dates[n])) }),
        }));
      if (dates[0]) opts.push({ key: "until", label: this.t("pause_until"), hint: this.t("pause_until_hint") });
      opts.push({ key: "indefinite", label: this.t("pause_indefinite"), hint: this.t("pause_indefinite_hint") });
      if (!opts.find((o) => o.key === m.choice)) m.choice = opts[0].key;
      const dateField = this.pauseDateField(sub);
      return `
        <h3 id="dash-modal-title" class="dash-modal__title">${esc(this.t("pause_title"))}</h3>
        <p class="dash-modal__intro">${esc(this.t("pause_intro"))}</p>
        ${this.savingsCallout(sub, "pause_saved", "pause_saved_new")}
        <div class="dash-opts" role="radiogroup">${opts
          .map((o) =>
            this.renderOpt(
              "pause-choice",
              o,
              m.choice === o.key,
              o.key === "until" && m.choice === "until" ? dateField : "",
            ),
          )
          .join("")}</div>
        <div class="dash-modal__actions">
          <button type="button" class="btn btn--amber" data-modal-pause-submit data-modal-focus>${esc(this.t("pause_submit"))}</button>
          <button type="button" class="btn btn--outline" data-modal-close>${esc(this.t("keep_running"))}</button>
        </div>`;
    }

    async submitPause(sub) {
      const choice = this.modal.choice;
      const dates = sub.upcoming_dates || [];
      if (choice === "until") {
        const until = this.modal.until || "";
        const range = this.pauseUntilRange(sub);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(until) || until < range.min || until > range.max) {
          this.modal.untilError = true;
          this.renderModal(false);
          this.modalEl.querySelector("[data-modal-until]")?.focus();
          return;
        }
        this.closeModal();
        return this.action(
          `/subscription/${sub.id}/reschedule`,
          { date: until },
          null,
          this.t("pause_done_until", { date: this.dateFmt.format(new Date(until + "T12:00:00")) }),
        );
      }
      this.closeModal();
      if (choice === "indefinite") return this.action(`/subscription/${sub.id}/pause`, {}, null, this.t("pause_done"));
      const n = parseInt(String(choice).replace("skip", ""), 10) || 1;
      const resume = dates[n] ? this.dateFmt.format(new Date(dates[n])) : "";
      return this.action(
        `/subscription/${sub.id}/skip-next`,
        { count: n },
        null,
        resume ? this.t("pause_done_skip", { date: resume }) : null,
      );
    }

    /* Kündigung: Grund → Alternative → Bestätigung */
    renderCancelModal(sub) {
      const m = this.modal;
      const step = m.step || "reason";
      const pct = sub.totals?.saving_pct || 0;
      const saved = this.fmt.format(num(sub.saved_so_far));
      const stepNo = { reason: 1, offer: 2, confirm: 3 }[step] || 1;
      const stepper = `<div class="dash-modal__step">${esc(this.t("cancel_step", { n: stepNo }))}</div>`;

      if (step === "reason") {
        /* Fragenkatalog: jeder Grund führt zu einem konkreten Angebot
           (siehe retentionOffers). "Nur einmal bestellen" erscheint nur,
           solange höchstens eine Folgelieferung stattfand — das ist die
           größte reale Kündigergruppe (33 % vor der 1. Folgelieferung).
           Dieselben Texte müssen in Seal-Admin → Kündigungsgründe
           stehen, damit die Auswertung konsistent bleibt. */
        const reasons = [
          ...(this.cancelFewDeliveries(sub) ? ["once"] : []),
          "stock",
          "no_need",
          "change",
          "price",
          "problem",
          "other",
        ];
        const noteField = this.cancelNoteField();
        const payAlert = this.paymentAlert(sub);
        return `${stepper}
          <h3 id="dash-modal-title" class="dash-modal__title">${esc(this.t("cancel_title"))}</h3>
          <p class="dash-modal__intro">${esc(this.t("cancel_intro"))}</p>
          ${
            payAlert
              ? `<div class="pay-alert pay-alert--bad dash-modal__pay"><strong>${esc(this.t("cancel_payment_hint"))}</strong> ${esc(payAlert)}
            <button type="button" class="dash-banner-link" data-payment-update>${esc(this.t("pay_update_now"))}</button></div>`
              : ""
          }
          ${!payAlert && num(sub.saved_so_far) > 0 ? `<p class="dash-saved">${this.t("reason_saved", { saved: `<strong>${esc(saved)}</strong>` })}</p>` : ""}
          <div class="dash-opts" role="radiogroup">${reasons
            .map((r) =>
              this.renderOpt(
                "cancel-reason",
                { key: r, label: this.t("reason_" + r) },
                m.reason === r,
                r === "other" && m.reason === "other" ? noteField : "",
              ),
            )
            .join("")}</div>
          <div class="dash-modal__actions">
            <button type="button" class="btn" data-modal-step="offer" ${this.canContinueCancel() ? "" : "disabled"} data-modal-focus>${esc(this.t("continue"))}</button>
            <button type="button" class="btn btn--outline" data-modal-close>${esc(this.t("keep_abo"))}</button>
          </div>`;
      }

      if (step === "offer") {
        const r = m.reason || "other";
        const offers = this.retentionOffers(sub, r);
        return `${stepper}
          <h3 id="dash-modal-title" class="dash-modal__title">${esc(this.t("offer_title_" + r))}</h3>
          <p class="dash-modal__intro">${esc(this.t("offer_text_" + r, { pct, saved }))}</p>
          <div class="dash-offers">${offers
            .map((o, i) =>
              o.key === "message" && m.msgOpen
                ? `
            <div class="dash-offer dash-offer--msg${i === 0 ? " dash-offer--primary" : ""}">
              <span class="dash-offer__label">${esc(o.label)}</span>
              <textarea class="dash-offer__msg" data-modal-msg rows="3" maxlength="1000" required placeholder="${esc(this.t("message_placeholder"))}">${esc(m.msg || "")}</textarea>
              <span class="dash-opt__req">${esc(this.t("message_required"))}</span>
              <button type="button" class="btn btn--block" data-modal-offer="message-send" ${String(m.msg || "").trim().length >= 5 ? "" : "disabled"}>${esc(this.t("message_send"))}</button>
            </div>`
                : `
            <button type="button" class="dash-offer${i === 0 ? " dash-offer--primary" : ""}" data-modal-offer="${esc(o.key)}" ${i === 0 ? "data-modal-focus" : ""}>
              <span class="dash-offer__label">${esc(o.label)}</span>
              ${o.hint ? `<span class="dash-offer__hint">${esc(o.hint)}</span>` : ""}
            </button>`,
            )
            .join("")}</div>
          <div class="dash-modal__actions dash-modal__actions--split">
            <button type="button" class="dash-modal__back" data-modal-step="reason">← ${esc(this.t("back"))}</button>
            <button type="button" class="dash-modal__cancel-link" data-modal-step="confirm">${esc(this.t("cancel_anyway"))}</button>
          </div>`;
      }

      const losses = [
        pct ? this.t("confirm_loss_discount", { pct }) : "",
        this.t("confirm_loss_loyalty"),
        this.t("confirm_loss_delivery", { interval: this.intervalBadge(sub.interval) }),
      ].filter(Boolean);
      return `${stepper}
        <h3 id="dash-modal-title" class="dash-modal__title">${esc(this.t("confirm_title"))}</h3>
        ${this.savingsCallout(sub, "confirm_saved", "confirm_saved_new")}
        <p class="dash-modal__intro">${esc(this.t("confirm_intro"))}</p>
        <ul class="dash-loss">${losses.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>
        <p class="dash-modal__note">${esc(this.t("confirm_note"))}</p>
        <div class="dash-modal__actions">
          <button type="button" class="btn" data-modal-close data-modal-focus>${esc(this.t("keep_abo"))}</button>
          <button type="button" class="btn btn--danger" data-modal-cancel-submit>${esc(this.t("confirm_submit"))}</button>
        </div>`;
    }

    /* Höchstens eine Folgelieferung nach der Erstbestellung? Dann ist
       "Ich wollte eigentlich nur einmal bestellen" der wahrscheinlichste
       Grund und steht ganz oben. completed_orders zählt die
       Erstbestellung mit. */
    cancelFewDeliveries(sub) {
      return num(sub.completed_orders) <= 2;
    }

    /* Weiter erst mit gewähltem Grund; bei "Anderer Grund" nur mit
       Freitext (mind. 5 Zeichen) — 443 von 759 bisherigen Gründen waren
       ein leeres "Anderer Grund". */
    canContinueCancel() {
      const m = this.modal || {};
      if (!m.reason) return false;
      if (m.reason === "other") return String(m.note || "").trim().length >= 5;
      return true;
    }

    /* Welche Alternative passt zum Grund? Reihenfolge = Priorität, die
       erste wird grün hervorgehoben. Bei pausierten Abos entfallen
       Skip und Pause (ergäbe keinen Sinn). */
    retentionOffers(sub, reason) {
      const dates = sub.upcoming_dates || [];
      const skip = {
        key: "skip",
        label: this.t("offer_skip"),
        hint: dates[1] ? this.t("offer_skip_hint", { date: this.dateFmt.format(new Date(dates[1])) }) : "",
      };
      const pause = { key: "pause", label: this.t("offer_pause"), hint: this.t("offer_pause_hint") };
      const edit = { key: "edit", label: this.t("offer_edit"), hint: this.t("offer_edit_hint") };
      const editLess = { key: "edit", label: this.t("offer_edit"), hint: this.t("offer_edit_less_hint") };
      /* Statt Kontakt-Link: Textfeld direkt im Dialog, Pflichtfeld,
         landet als Notiz am Abo (POST /message). */
      const contact = { key: "message", label: this.t("offer_message"), hint: this.t("offer_message_hint") };
      const interval = this.intervalOptions(sub)
        .slice(0, 1)
        .map((iv) => ({
          key: `interval:${iv.every}:${iv.unit}`,
          label: this.t("offer_interval", { label: this.intervalBadge(iv) }),
          hint: this.t("offer_interval_hint"),
        }));
      /* Längster Rhythmus zuerst (für "nur einmal bestellen") */
      const intervalLong = this.intervalOptions(sub)
        .slice()
        .reverse()
        .slice(0, 1)
        .map((iv) => ({
          key: `interval:${iv.every}:${iv.unit}`,
          label: this.t("offer_interval", { label: this.intervalBadge(iv) }),
          hint: this.t("offer_interval_hint"),
        }));
      const skip2 = dates[2]
        ? {
            key: "skip2",
            label: this.t("offer_skip_n", { n: 2 }),
            hint: this.t("offer_skip_n_hint", { date: this.dateFmt.format(new Date(dates[2])) }),
          }
        : null;
      /* Treue-Vorschau als Angebot, solange die nächste Rabattstufe
         noch nicht erreicht ist */
      const pd = sub.pending_discount;
      const done = num(sub.completed_orders);
      const loyalty =
        pd && pd.pct && pd.from_cycle > done
          ? {
              key: "loyalty",
              label: this.t("offer_loyalty", { cycle: pd.from_cycle }),
              hint: this.t("offer_loyalty_hint", { left: pd.from_cycle - done, pct: pd.pct }),
            }
          : null;
      const seg = this.segment();
      const b2b =
        seg === "b2b" || seg === "firmenkunde"
          ? { key: "message", label: this.t("offer_b2b"), hint: this.t("offer_b2b_hint") }
          : null;
      const payment = this.paymentAlert(sub)
        ? { key: "payment", label: this.t("pay_update_now"), hint: this.paymentAlert(sub) }
        : null;
      const by = {
        once: [...intervalLong, pause, skip],
        stock: [skip, skip2, ...interval],
        no_need: [edit, pause],
        change: [edit, contact],
        price: [loyalty, ...interval, b2b || contact],
        problem: [payment, contact, pause],
        other: [contact, pause, skip],
        /* alter Katalog, falls eine Session ihn noch offen hat */
        less_items: [...interval, editLess, skip],
        wrong_items: [edit, contact, pause],
        skip: [skip, pause, ...interval],
      };
      let offers = (by[reason] || by.other).filter(Boolean);
      if (sub.status === "paused")
        offers = offers.filter((o) => o.key !== "skip" && o.key !== "skip2" && o.key !== "pause");
      if (!offers.length) offers = [edit, contact].filter(Boolean);
      return offers;
    }

    /* Verlängerte Rhythmen zum aktuellen Intervall: 2× und 3× (z. B.
       "Jeden Monat" → "Alle 2 Monate" / "Alle 3 Monate"). */
    intervalOptions(sub) {
      const iv = sub.interval || {};
      const every = num(iv.every);
      const unit = String(iv.unit || "week").replace(/s$/, "");
      if (!every) return [];
      const max = { day: 30, week: 12, month: 12, year: 1 }[unit] || 12;
      return [every * 2, every * 3].filter((n) => n <= max).map((n) => ({ every: n, unit }));
    }

    async applyOffer(sub, key) {
      if (key === "pause") {
        this.modal = { type: "pause" };
        return this.renderModal(true);
      }
      if (key === "skip") {
        this.closeModal();
        return this.action(`/subscription/${sub.id}/skip-next`, { count: 1 });
      }
      if (key === "skip2") {
        this.closeModal();
        return this.action(`/subscription/${sub.id}/skip-next`, { count: 2 });
      }
      if (key === "payment") {
        this.closeModal();
        return this.action(`/subscription/${sub.id}/payment-update`, {}, null, this.t("pay_update_sent"));
      }
      if (key === "loyalty") {
        this.closeModal();
        const el = this.querySelector(".dash-loyalty");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("is-flash");
          setTimeout(() => el.classList.remove("is-flash"), 2400);
        }
        return;
      }
      if (key === "edit") {
        this.closeModal();
        const input = this.querySelector("[data-add-search]");
        if (input) {
          input.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => input.focus({ preventScroll: true }), 400);
        }
        return;
      }
      if (key === "message") {
        /* Kachel aufklappen: Textfeld + Senden; vorhandener Freitext
           aus Schritt 1 wird übernommen */
        this.modal.msgOpen = true;
        if (!this.modal.msg && this.modal.note) this.modal.msg = this.modal.note;
        this.renderModal(false);
        const ta = this.modalEl.querySelector("[data-modal-msg]");
        if (ta) {
          ta.focus();
          ta.setSelectionRange(ta.value.length, ta.value.length);
        }
        return;
      }
      if (key === "message-send") {
        const text = String(this.modal.msg || "").trim();
        if (text.length < 5) return;
        const reason = this.modal.reason || "";
        this.closeModal();
        return this.action(`/subscription/${sub.id}/message`, { text, reason }, null, this.t("message_sent"));
      }
      if (key.startsWith("interval:")) {
        const [, every, unit] = key.split(":");
        this.closeModal();
        return this.action(
          `/subscription/${sub.id}/interval`,
          { every: parseInt(every, 10), unit },
          null,
          this.t("interval_done"),
        );
      }
    }

    async submitCancel(sub) {
      const { reason, note, msg } = this.modal;
      this.closeModal();
      const text = [(note || "").trim(), (msg || "").trim()].filter((t, i, a) => t && a.indexOf(t) === i).join(" — ");
      return this.action(
        `/subscription/${sub.id}/cancel`,
        { reason: reason || "", note: text },
        null,
        this.t("cancel_done"),
      );
    }

    /* ---------- Helfer ------------------------------------------------ */

    intervalLabel(interval) {
      if (!interval || !interval.every) return "";
      const n = num(interval.every);
      const unit = String(interval.unit || "week").replace(/s$/, "");
      if (n === 1) return this.t("interval_each_" + unit); /* "Jeden Monat" statt "Lieferung alle Monat" */
      return this.t("interval_every") + " " + n + " " + this.t("unit_" + unit + "_other");
    }

    /* Kurzform fürs Übersichts-Badge: "Jeden Monat" / "Alle 2 Wochen" */
    intervalBadge(interval) {
      if (!interval || !interval.every) return "";
      const n = num(interval.every);
      const unit = String(interval.unit || "week").replace(/s$/, "");
      if (n === 1) return this.t("interval_each_" + unit);
      return this.t("interval_every_n", { n, unit: this.t("unit_" + unit + "_other") });
    }

    showEmpty() {
      this.hide("[data-dash-root]");
      const box = this.querySelector("[data-dash-empty]");
      box.innerHTML = `<p>${esc(this.t("empty"))}</p>
        <a class="altruan-dash__btn" href="${esc(this.cfg.urls.collection)}">${esc(this.t("empty_cta"))}</a>`;
      box.hidden = false;
    }

    toast(msg, isError) {
      const el = document.createElement("div");
      el.className = "dash-toast" + (isError ? " dash-toast--error" : "");
      el.textContent = msg;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3500);
    }

    hide(sel) {
      const n = this.querySelector(sel);
      if (n) n.hidden = true;
    }
  }

  customElements.define("altruan-abo-dashboard", AltruanAboDashboard);
})();
